import { useState } from 'react'
import { useForm, router } from '@inertiajs/react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { ManagementCopy } from '@/frontend/features/Fde/managementTypes'
import type { TaskCategory } from '@/frontend/features/Fde/projectTaskTypes'

interface Props {
  projectId: string
  categories: TaskCategory[]
  copy: ManagementCopy
}

export default function TaskCategoriesManager({ projectId, categories, copy }: Props) {
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<TaskCategory | null>(null)
  const form = useForm({ name: '' })
  function edit(category: TaskCategory | null) {
    setEditing(category)
    form.setData('name', category?.name ?? '')
    form.clearErrors()
  }
  function save(event: React.FormEvent) {
    event.preventDefault()
    form.transform((data) => ({
      task_category: { ...data, ...(editing ? { lock_version: editing.lock_version } : {}) },
    }))
    const options = {
      preserveScroll: true,
      onSuccess: (page: { props: Record<string, unknown> }) => {
        if (!(page.props.flash as { alert?: string } | undefined)?.alert) edit(null)
      },
    }
    const path = fdeProjectPath(
      `${fdeRoutes.projectTaskCategories}${editing ? `/${editing.id}` : ''}`,
      projectId,
    )
    if (editing) form.patch(path, options)
    else form.post(path, options)
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value)
        edit(null)
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          {copy.task_categories}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{copy.task_categories}</DialogTitle>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto divide-y divide-border/50">
          {categories.map((category) => (
            <div key={category.id} className="flex items-center gap-2 py-2">
              <span className="flex-1 text-sm">{category.name}</span>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label={`${copy.edit_category}: ${category.name}`}
                onClick={() => edit(category)}
              >
                <Pencil className="size-3.5" />
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label={`${copy.delete_category}: ${category.name}`}
                onClick={() => {
                  if (window.confirm(copy.confirm_delete_category))
                    router.delete(
                      fdeProjectPath(
                        `${fdeRoutes.projectTaskCategories}/${category.id}`,
                        projectId,
                      ),
                      { preserveScroll: true },
                    )
                }}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))}
          {!categories.length && (
            <p className="py-4 text-sm text-muted-foreground">{copy.no_categories}</p>
          )}
        </div>
        <form onSubmit={save} className="flex gap-2 border-t pt-4">
          <Input
            aria-label={copy.category_name}
            placeholder={copy.category_name}
            value={form.data.name}
            onChange={(event) => form.setData('name', event.target.value)}
            maxLength={80}
          />
          <Button type="submit" disabled={form.processing || !form.data.name.trim()}>
            {editing ? (
              copy.save_category
            ) : (
              <>
                <Plus className="size-4" />
                {copy.add_category}
              </>
            )}
          </Button>
        </form>
        {editing && (
          <Button variant="ghost" size="sm" onClick={() => edit(null)}>
            {copy.cancel_category_edit}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  )
}
